import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import {
  ArrowLeftRight, Briefcase, CalendarCheck, ChevronDown, FileText, Landmark, Pencil, Plane,
  TrendingUp, UserMinus, UserPen, Users,
} from 'lucide-react'
import {
  CurrencyValue, DateValue, DetailTabs, EmptyState, ErrorState, KpiGrid, PageHeader,
  PermissionGate,
} from '@/components/common'
import type { Kpi } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePermission } from '@/hooks/usePermission'
import { EmployeeDocumentsTab } from '@/features/document'
import { useEmployeeLeaveBalances } from '@/features/leave'
import { monthName, useEmployeeProvisions } from '@/features/payroll'
import { useAssignments, useDirectReports, useEmployee, useEmployeeTimeline } from '../api'
import { AssignmentTimeline } from '../components/AssignmentTimeline'
import { BankAccountsTab } from '../components/BankAccountsTab'
import { DeactivateEmployeeDialog } from '../components/DeactivateEmployeeDialog'
import { EditEmployeeDialog } from '../components/EditEmployeeDialog'
import { EntitlementsTab } from '../components/EntitlementsTab'
import { LeaveBalanceTab } from '../components/LeaveBalanceTab'
import { PayTab } from '../components/PayTab'
import { InviteToHumisButton } from '../components/InviteToHumisButton'
import { ProfileHeaderCard } from '../components/ProfileHeaderCard'
import { RecordPlacementDialog } from '../components/RecordPlacementDialog'
import { serviceLength } from '../components/serviceLength'

/** A labelled fact. Renders an em dash rather than a blank when there is nothing on file. */
function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-[var(--color-muted)]">{label}</dt>
      <dd className="text-sm text-[var(--color-heading)]">{children ?? '—'}</dd>
    </div>
  )
}

function humanise(value: string | null | undefined): string | null {
  return value ? value.toLowerCase().replace(/_/g, ' ') : null
}

function Section({
  title, icon: Icon, action, children,
}: {
  title: string
  icon?: typeof Users
  action?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-heading)]">
          {Icon && <Icon className="size-4" aria-hidden="true" />}
          {title}
        </h3>
        {action}
      </div>
      {children}
    </Card>
  )
}

type PlacementReason = 'TRANSFER' | 'PROMOTION'

/** A whole-unit amount with its currency code, for a stat card that takes text only. */
function money(amount: number, currency: string | null): string {
  const figure = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(amount)
  return currency ? `${currency} ${figure}` : figure
}

function longDate(iso: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * One person's profile — the page every link to an employee lands on.
 *
 * <p>The placement shown is the assignment <b>in force today</b>, from the assignment
 * history rather than a column on the employee: somebody who moves department next month
 * already has that assignment on file, and showing it now would be wrong.
 *
 * <p>Designation, grade, department and manager are changed through "Change job", which
 * records a dated move. The plain edit form cannot change them, and the server refuses it
 * too, because overwriting the row would rewrite last month's placement as well.
 *
 * <p>The headline figures are permission-gated one by one: leave balances need
 * {@code leave:approve} and gratuity and tickets need {@code payroll:read-all}. A card that is
 * not allowed is left out rather than shown empty — an empty gratuity card reads as "owed
 * nothing".
 */
export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const employee = useEmployee(id)
  const assignments = useAssignments(id)
  const timeline = useEmployeeTimeline(id)
  const reports = useDirectReports(id)
  const canReadPay = usePermission('payroll:read-all')
  const canReadLeave = usePermission('leave:approve')
  const canWrite = usePermission('employee:write')
  const leave = useEmployeeLeaveBalances(id, new Date().getFullYear(), canReadLeave)
  const provisions = useEmployeeProvisions(id, canReadPay)

  const [editing, setEditing] = useState(false)
  const [placement, setPlacement] = useState<{ reason: PlacementReason; key: number } | null>(null)
  const [ending, setEnding] = useState(false)

  const current = assignments.data?.find((assignment) => assignment.current)
  const name = employee.data?.summary.displayName
  const breadcrumb = useBreadcrumb(name ?? 'Profile')

  const changeJob = (reason: PlacementReason) =>
    setPlacement({ reason, key: Date.now() })

  if (employee.isError) {
    return (
      <div className="space-y-4">
        <PageHeader title="Profile" breadcrumb={breadcrumb} />
        <ErrorState error={employee.error} onRetry={() => void employee.refetch()} />
      </div>
    )
  }

  const data = employee.data
  const service = serviceLength(data?.summary.joinDate)

  // Annual leave is the balance people ask about; fall back to the total of limited types.
  const annual = leave.data?.find((balance) =>
    /^(AL|ANNUAL)/i.test(balance.leaveTypeCode) || /annual/i.test(balance.leaveTypeName))
  const bounded = leave.data?.filter((balance) => !balance.unbounded) ?? []
  const provisionOf = (type: string) =>
    provisions.data?.balances.find((balance) => balance.provisionType === type)
  const gratuity = provisionOf('GRATUITY')
  const ticket = provisionOf('AIR_TICKET')

  const kpis: Kpi[] = [
    {
      label: 'Length of service',
      value: service?.label ?? '—',
      caption: data?.summary.joinDate ? `Since ${longDate(data.summary.joinDate)}` : 'No join date on file',
      icon: Briefcase,
      tint: 'purple',
    },
    ...(canReadLeave
      ? [{
        label: annual ? `${annual.leaveTypeName} available` : 'Leave available',
        value: leave.isPending ? '…'
          : annual ? `${annual.available} days`
            : bounded.length ? `${bounded.reduce((sum, b) => sum + b.available, 0)} days` : '—',
        caption: annual
          ? `${annual.used} used · ${annual.pending} pending of ${annual.entitled}`
          : leave.isSuccess && leave.data.length === 0 ? 'No balances this year' : 'All limited leave types',
        icon: CalendarCheck,
        tint: 'green' as const,
      }]
      : []),
    ...(canReadPay
      ? [
        {
          label: 'Gratuity accrued',
          value: provisions.isPending ? '…'
            : gratuity ? money(gratuity.closingBalance, gratuity.currency) : '—',
          caption: gratuity
            ? `As of ${monthName(gratuity.periodYear, gratuity.periodMonth)}`
            : 'Not accrued yet',
          icon: Landmark,
          tint: 'orange' as const,
        },
        {
          label: 'Air ticket balance',
          value: provisions.isPending ? '…'
            : ticket ? money(ticket.closingBalance, ticket.currency) : '—',
          caption: provisions.data?.airTicketEntitlement
            ? `Every ${provisions.data.airTicketEntitlement.frequencyMonths} months · ${humanise(provisions.data.airTicketEntitlement.cabinClass)}`
            : 'No entitlement applies',
          icon: Plane,
          tint: 'blue' as const,
        },
      ]
      : [{
        label: 'Direct reports',
        value: reports.data?.length ?? '…',
        caption: 'People reporting to them',
        icon: Users,
        tint: 'blue' as const,
      }]),
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={name ?? 'Profile'}
        breadcrumb={breadcrumb}
        subtitle={data
          ? `${data.summary.employeeCode} · ${data.summary.designationName ?? 'No designation on file'}`
          : undefined}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => { void navigate(`/employees/${id!}/assignments`) }}
            >
              <ArrowLeftRight className="size-4" aria-hidden="true" />
              Placement & transfers
            </Button>
            <PermissionGate permission="employee:write">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" disabled={!data}>
                    <Pencil className="size-4" aria-hidden="true" />
                    Edit
                    <ChevronDown className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[16rem]">
                  <DropdownMenuLabel>Edit employee</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => setEditing(true)}>
                    <UserPen className="size-4" aria-hidden="true" />
                    Personal & contract details
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => changeJob('PROMOTION')}>
                    <TrendingUp className="size-4" aria-hidden="true" />
                    Change designation / grade
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => changeJob('TRANSFER')}>
                    <ArrowLeftRight className="size-4" aria-hidden="true" />
                    Change department, location or manager
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </PermissionGate>
            {data && <InviteToHumisButton employeeId={id!} />}
            <PermissionGate permission="exit:write">
              <Button
                type="button"
                variant="outline"
                disabled={!data || data.summary.status === 'EXITED'}
                onClick={() => setEnding(true)}
              >
                <UserMinus className="size-4" aria-hidden="true" />
                Deactivate
              </Button>
            </PermissionGate>
          </>
        }
      />

      {data && (
        <>
          <EditEmployeeDialog employee={data} open={editing} onOpenChange={setEditing} />
          {placement && (
            <RecordPlacementDialog
              key={placement.key}
              employeeId={id!}
              current={current}
              initialReason={placement.reason}
              open
              onOpenChange={(open) => { if (!open) setPlacement(null) }}
            />
          )}
          <DeactivateEmployeeDialog
            employeeId={id!}
            employeeName={data.summary.displayName}
            open={ending}
            onOpenChange={setEnding}
          />
        </>
      )}

      {employee.isPending && (
        <>
          <Skeleton className="h-32" />
          <Skeleton className="h-24" />
        </>
      )}

      {data && (
        <>
          <ProfileHeaderCard employee={data} current={current} />
          <KpiGrid items={kpis} />

          <DetailTabs
            tabs={[
              {
                key: 'overview',
                label: 'Overview',
                content: (
                  <div className="grid gap-4 lg:grid-cols-2">
                    <Section title="Personal" icon={UserPen}>
                      <dl className="grid grid-cols-2 gap-3">
                        <Fact label="Full name">
                          {[data.firstName, data.middleName, data.lastName].filter(Boolean).join(' ')}
                        </Fact>
                        <Fact label="Employee code">{data.summary.employeeCode}</Fact>
                        <Fact label="Gender">
                          {humanise(data.gender) && <span className="capitalize">{humanise(data.gender)}</span>}
                        </Fact>
                        <Fact label="Date of birth">
                          {data.dateOfBirth ? <DateValue value={data.dateOfBirth} /> : null}
                        </Fact>
                        <Fact label="Nationality">{data.nationality}</Fact>
                        <Fact label="Marital status">
                          {humanise(data.maritalStatus) && <span className="capitalize">{humanise(data.maritalStatus)}</span>}
                        </Fact>
                        <Fact label="Personal email">{data.personalEmail}</Fact>
                        <Fact label="Personal phone">{data.personalPhone}</Fact>
                      </dl>
                    </Section>

                    <Section
                      title="Job & placement today"
                      icon={Briefcase}
                      action={canWrite && (
                        <Button type="button" size="sm" variant="outline" onClick={() => changeJob('PROMOTION')}>
                          <Pencil className="size-4" aria-hidden="true" />
                          Change
                        </Button>
                      )}
                    >
                      {assignments.isPending && <Skeleton className="h-24" />}
                      {assignments.isSuccess && !current && (
                        <p className="text-sm text-[var(--color-muted)]">
                          No placement is in force today. Use Change to record the designation,
                          grade and department this person holds.
                        </p>
                      )}
                      {current && (
                        <dl className="grid grid-cols-2 gap-3">
                          <Fact label="Designation">{current.designationName}</Fact>
                          <Fact label="Grade">{current.gradeName}</Fact>
                          <Fact label="Department">{current.departmentName}</Fact>
                          <Fact label="Division">{current.divisionName}</Fact>
                          <Fact label="Location">{current.locationName}</Fact>
                          <Fact label="Cost centre">{current.costCenterName}</Fact>
                          <Fact label="Reports to">{current.managerName}</Fact>
                          <Fact label="Establishment">{data.establishmentName}</Fact>
                          <Fact label="In force since"><DateValue value={current.effectiveFrom} /></Fact>
                        </dl>
                      )}
                    </Section>

                    <Section title="Employment & contract" icon={FileText}>
                      <dl className="grid grid-cols-2 gap-3">
                        <Fact label="Employment type">
                          <span className="capitalize">{humanise(data.summary.employmentType)}</span>
                        </Fact>
                        <Fact label="Contract type">{data.contractType}</Fact>
                        <Fact label="Joined">
                          {data.summary.joinDate ? <DateValue value={data.summary.joinDate} /> : null}
                        </Fact>
                        <Fact label="Contract ends">
                          {data.contractEndDate ? <DateValue value={data.contractEndDate} /> : null}
                        </Fact>
                        <Fact label="Probation ends">
                          {data.probationEndDate ? <DateValue value={data.probationEndDate} /> : null}
                        </Fact>
                        <Fact label="Confirmed on">
                          {data.confirmationDate ? <DateValue value={data.confirmationDate} /> : null}
                        </Fact>
                        <Fact label="Notice period">
                          {data.noticePeriodDays != null ? `${data.noticePeriodDays} days` : null}
                        </Fact>
                        <Fact label="Exit date">
                          {data.exitDate ? <DateValue value={data.exitDate} /> : null}
                        </Fact>
                      </dl>
                    </Section>

                    <Section title="Direct reports" icon={Users}>
                      {reports.isPending && <Skeleton className="h-16" />}
                      {reports.isSuccess && reports.data.length === 0 && (
                        <p className="text-sm text-[var(--color-muted)]">Nobody reports to them.</p>
                      )}
                      {reports.isSuccess && reports.data.length > 0 && (
                        <ul className="space-y-1 text-sm">
                          {reports.data.map((person) => (
                            <li key={person.id}>
                              <Link
                                to={`/employees/${person.id}`}
                                className="text-[var(--color-primary)] hover:underline"
                              >
                                {person.displayName}
                              </Link>
                              <span className="ms-2 text-xs text-[var(--color-muted)]">
                                {person.designationName ?? '—'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Section>

                    {canReadPay && gratuity && (
                      <Section title="Gratuity" icon={Landmark}>
                        <p className="text-2xl font-semibold tabular-nums text-[var(--color-heading)]">
                          <CurrencyValue value={gratuity.closingBalance} currency={gratuity.currency ?? undefined} />
                        </p>
                        <p className="mt-1 text-xs text-[var(--color-muted)]">
                          Accrued through {monthName(gratuity.periodYear, gratuity.periodMonth)}. Full detail on the
                          Gratuity & tickets tab.
                        </p>
                      </Section>
                    )}

                    <div className={canReadPay && gratuity ? '' : 'lg:col-span-2'}>
                      <Section title="Timeline">
                        {timeline.isPending && <Skeleton className="h-24" />}
                        {timeline.isSuccess && timeline.data.length === 0 && (
                          <EmptyState
                            title="Nothing recorded yet"
                            description="Hires, transfers and confirmations write an entry here as they happen."
                          />
                        )}
                        {timeline.isSuccess && timeline.data.length > 0 && (
                          <ol className="space-y-3">
                            {timeline.data.map((entry) => (
                              <li key={entry.id} className="border-s-2 border-[var(--color-border)] ps-3">
                                <p className="text-sm font-medium text-[var(--color-heading)]">{entry.title}</p>
                                {entry.detail && (
                                  <p className="text-xs text-[var(--color-muted)]">{entry.detail}</p>
                                )}
                                <DateValue value={entry.eventDate} className="text-xs text-[var(--color-muted)]" />
                              </li>
                            ))}
                          </ol>
                        )}
                      </Section>
                    </div>
                  </div>
                ),
              },
              {
                key: 'placement',
                label: 'Job history',
                content: (
                  <Card className="p-4">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-[var(--color-muted)]">
                        Every change of designation, grade or department is a new dated row, so
                        past months keep showing where this person actually sat.
                      </p>
                      <PermissionGate permission="employee:write">
                        <div className="flex gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => changeJob('TRANSFER')}>
                            <ArrowLeftRight className="size-4" aria-hidden="true" />
                            Transfer
                          </Button>
                          <Button type="button" size="sm" onClick={() => changeJob('PROMOTION')}>
                            <TrendingUp className="size-4" aria-hidden="true" />
                            Change designation / grade
                          </Button>
                        </div>
                      </PermissionGate>
                    </div>
                    {assignments.isPending && <Skeleton className="h-32" />}
                    {assignments.isSuccess && <AssignmentTimeline assignments={assignments.data} />}
                    <div className="mt-3">
                      <Link
                        to={`/employees/${id!}/assignments`}
                        className="text-sm text-[var(--color-primary)] hover:underline"
                      >
                        Open placement and transfers
                      </Link>
                    </div>
                  </Card>
                ),
              },
              ...(canReadLeave
                ? [{ key: 'leave', label: 'Leave', content: <LeaveBalanceTab employeeId={id!} /> }]
                : []),
              ...(canReadPay
                ? [
                  { key: 'pay', label: 'Pay', content: <PayTab employeeId={id!} /> },
                  { key: 'entitlements', label: 'Gratuity & tickets', content: <EntitlementsTab employeeId={id!} /> },
                ]
                : []),
              {
                key: 'documents',
                label: 'Documents',
                content: (
                  <>
                    <EmployeeDocumentsTab employeeId={id!} />
                    <p className="mt-3 flex items-center gap-2 text-sm text-[var(--color-muted)]">
                      <FileText className="size-4" aria-hidden="true" />
                      Numbers are hidden until somebody asks for one, and every reveal is
                      written to the access log.
                    </p>
                  </>
                ),
              },
              { key: 'bank', label: 'Bank', content: <BankAccountsTab employeeId={id!} /> },
            ]}
          />
        </>
      )}
    </div>
  )
}
